// In-progress Twilio VoIP call state — pure client state shared across the
// banner, the modal, and the originating view.
//
// Idiomatic zustand shape per `.claude/rules/withings-conventions.md`
// "Zustand stores": actions call set() directly, no internal_* ceremony.
//
// Mirrors wpc-front-master/src/js/stores/Twilio.js for the underlying
// Twilio Device lifecycle.

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Device } from '@twilio/voice-sdk';
import PatientService from '@/services/PatientService';

// eslint-disable-next-line no-console -- DEV-only Twilio event tracing
const trace = (msg: string) => { if (import.meta.env.DEV) console.log(msg); };

// Twilio SDK is ~600 KB minified. Lazy-load on first init() call so
// users who never place a call don't pay the cost.
const loadTwilioDevice = (): Promise<typeof Device> => (
  import('@twilio/voice-sdk').then((m) => m.Device)
);

/** Call status constants. */
export const CALL_STATUS = {
  DISCONNECTED: 'disconnected',
  DIALING: 'dialing',
  CONNECTED: 'connected',
  REJECTED: 'rejected',
} as const;

export type CallStatus = typeof CALL_STATUS[keyof typeof CALL_STATUS];

export interface CallingPatient {
  name: string;
  phone: string;
  userid: string | number;
}

/**
 * Loose shape for the patient passed to `startCall`. Concrete consumers
 * pass full patient records from various sources (dashboard rows, Withings
 * roster entries) — we only read the fields we use.
 */
export interface CallablePatient {
  firstname?: string;
  lastname?: string;
  userid: string | number;
  phone_number?: string;
  phone?: string;
}

export interface CallState {
  callStatus: CallStatus;
  callingPatient: CallingPatient | null;
  /** Timestamp (Date.now()) when the call reached CONNECTED. */
  callStartTime: number | null;
  twilio: Device | null;

  /** Initialise the Twilio Device — fetches token, registers (no await). */
  init: () => Promise<Device>;
  /** Place a call against an initialised Device. */
  call: (number: string) => Promise<Device | null>;
  /** High-level: init device + place call. */
  startCall: (patient: CallablePatient) => Promise<void>;
  /** Hang up the active call via Device.disconnectAll(). */
  disconnect: () => void;
  /** Tear down the Device after a 2s grace period; reset state. */
  closeCall: () => void;
}

const useCallStore = create<CallState>()(
  devtools((set, get) => ({
    // ── State ────────────────────────────────────────────────────────────
    callStatus: CALL_STATUS.DISCONNECTED,
    callingPatient: null,
    callStartTime: null,
    twilio: null,

    // ── Actions ──────────────────────────────────────────────────────────

    /** Initialise the Twilio Device — fetches token, registers. */
    init: async () => {
      const [accessToken, DeviceCtor] = await Promise.all([
        PatientService.getVoiceAccessToken(),
        loadTwilioDevice(),
      ]);

      // The Twilio SDK accepts these flags via constructor options but its
      // typings don't expose all of them — cast through `unknown` to keep
      // the call site honest about the type gap.
      const twilio = new DeviceCtor(accessToken, {
        debug: import.meta.env.DEV,
        answerOnBridge: true,
        codecPreferences: ['opus', 'pcmu'],
      } as unknown as ConstructorParameters<typeof DeviceCtor>[1]);

      twilio.on('registered', () => {
        trace('[Twilio] Device registered — ready to make calls');
      });

      twilio.on('error', (error: { message?: string }) => {
        console.error('[Twilio] Device error:', error.message ?? error);
      });

      twilio.register(); // do NOT await
      set({ twilio });
      return twilio;
    },

    /** Place a call against an initialised Device. */
    call: async (number) => {
      const { twilio, closeCall } = get();
      if (!twilio) return null;

      const activeCall = await twilio.connect({
        params: { To: number },
      });

      set({ callStatus: CALL_STATUS.DIALING });

      activeCall.on('accept', () => {
        trace('[Twilio] Call accepted');
        set({ callStatus: CALL_STATUS.CONNECTED, callStartTime: Date.now() });
      });

      activeCall.on('reject', () => {
        trace('[Twilio] Call rejected');
        set({ callStatus: CALL_STATUS.REJECTED });
        closeCall();
      });

      activeCall.on('disconnect', () => {
        trace('[Twilio] Call disconnected');
        set({ callStatus: CALL_STATUS.DISCONNECTED });
        closeCall();
      });

      activeCall.on('cancel', () => {
        trace('[Twilio] Call cancelled');
        set({ callStatus: CALL_STATUS.DISCONNECTED });
        closeCall();
      });

      return twilio;
    },

    /** High-level: init device + call. Used by the urgent-patients flow. */
    startCall: async (patient) => {
      const phone = patient.phone_number ?? patient.phone;
      if (!phone) return;

      set({
        callingPatient: {
          name: `${patient.firstname ?? ''} ${patient.lastname ?? ''}`.trim(),
          phone,
          userid: patient.userid,
        },
        callStatus: CALL_STATUS.DIALING,
        callStartTime: null,
      });

      try {
        await get().init();
        await get().call(phone);
      } catch (err) {
        console.error('[Twilio] Failed to start call:', err);
        set({ callStatus: CALL_STATUS.DISCONNECTED });
        get().closeCall();
      }
    },

    disconnect: () => {
      const { twilio } = get();
      if (twilio) {
        twilio.disconnectAll();
      }
      get().closeCall();
    },

    closeCall: () => {
      setTimeout(() => {
        const { twilio } = get();
        if (twilio) {
          try { twilio.destroy(); } catch { /* ignore */ }
        }
        set({
          twilio: null,
          callingPatient: null,
          callStartTime: null,
          callStatus: CALL_STATUS.DISCONNECTED,
        });
      }, 2000);
    },
  }), { name: 'useCallStore' }),
);

// ── Atomic selector hooks — preferred consumer entry points ─────────────────
// New code should import these named hooks rather than calling
// `useCallStore((s) => s.x)` directly.

export const useCallStatus = () => useCallStore((s) => s.callStatus);
export const useCallingPatient = () => useCallStore((s) => s.callingPatient);
export const useCallStartTime = () => useCallStore((s) => s.callStartTime);

export default useCallStore;
